import { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import { BsPinAngleFill } from 'react-icons/bs';
import { useChat } from '../../contexts/ChatContext';

/**
 * Pinned messages, shown above the conversation.
 *
 * Collapsed to a single line by default — a pin bar that eats half the panel is
 * worse than no pin bar. Expands to show all of them.
 */
export default function PinnedBar() {
  const { pins, togglePin } = useChat();
  const [expanded, setExpanded] = useState(false);

  if (pins.length === 0) return null;

  const visible = expanded ? pins : pins.slice(0, 1);

  const preview = (pin) => {
    if (pin.deletedAt) return 'This message was deleted.';
    if (!pin.body) return pin.hasAttachments ? 'Attachment' : '';
    return pin.body;
  };

  return (
    <div className="border-b border-neutral-300 bg-neutral-100">
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 mb-1">
          <BsPinAngleFill className="w-3 h-3 text-secondary shrink-0" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-primary/60">
            {pins.length === 1 ? 'Pinned' : `${pins.length} pinned`}
          </span>

          {pins.length > 1 && (
            <button
              onClick={() => setExpanded((open) => !open)}
              className="ml-auto flex items-center gap-0.5 text-[11px] text-primary/50 hover:text-primary transition-colors"
            >
              {expanded ? 'Show less' : 'Show all'}
              {expanded ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>

        <ul className="space-y-1">
          {visible.map((pin) => (
            <li key={pin.messageId} className="flex items-start gap-2 group">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-primary/85 truncate">
                  <span className="font-medium">{pin.senderName || 'Unknown member'}: </span>
                  {preview(pin)}
                </p>
                {expanded && (
                  <p className="text-[10px] text-primary/40 mt-0.5">
                    Pinned by {pin.pinnedByName || 'a member'}
                  </p>
                )}
              </div>

              <button
                onClick={() => togglePin({ id: pin.messageId, isPinned: true })}
                className="p-0.5 rounded-full text-primary/40 hover:text-primary hover:bg-primary/10
                           opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all shrink-0"
                aria-label="Unpin this message"
                title="Unpin"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
